import React, { useEffect, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { DisplayAdsEditor } from '@bingads-webui-campaign-react/display-ads-editor';
import { ClarityTags, useClarityTags, AdditionalQueries } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { DataContext } from '@bingads-webui-campaign-react/ads-studio-shared-components';

import { useCreateDisplayTemplatePageStyles } from './style';
import { useNotify } from './use-notify';

const CreateDisplayTemplatePage = ({
  i18n,
  displayTemplatesDataService,
  brandKitDataService,
  permissions,
  aid,
  cid,
  brandkitApiState,
  oDataConfig,
  serverContext,
  dataService,
  preferenceService,
  navigation,
  instrumentation,
  logger,
  search,
  pagePerfMarker: displayTemplateCreatePagePerfMarker,
  setDataLoaded,
  notificationApi,
}) => {
  const classes = useCreateDisplayTemplatePageStyles();
  const [templateDetails, setTemplateGroupDetails] = useState(null);
  const { data: detailsData } = useContext(DataContext);
  const queries = queryString.parse(search);
  const { templateGroupId = '' } = queries || {};
  const templateVersion = '1.3';
  useEffect(() => {
    if (!templateDetails) {
      if (!detailsData && templateGroupId) {
        displayTemplatesDataService.fetchDisplayAdsTemplateGroupDetails([templateGroupId], templateVersion).then(({ data }) => {
          const details = data.details.find(detail => detail.templateGroupId === templateGroupId);
          setTemplateGroupDetails({ templateGroupDetails: details });
        }).catch(() => {
          notificationApi.showWarning(i18n.getString(_TL_('Failed to load. Please refresh the page and try again.')));
        });
      } else {
        setTemplateGroupDetails(detailsData);
      }
    }
  }, [detailsData, templateGroupId, templateDetails, displayTemplatesDataService, i18n, notificationApi, templateVersion]);

  const notify = useNotify({
    i18n, navigation, logger,
  });

  useEffect(() => {
    if (!detailsData && !templateGroupId) {
      navigation.gotoDisplayTemplatesPage({ removeSearchQuery: [AdditionalQueries.TemplateGroupId] });
    }
  }, [detailsData, navigation, templateGroupId]);

  useClarityTags([
    ClarityTags.LaunchedAdsStudio,
    ClarityTags.CreateDisplayTemplate,
  ]);

  if (templateDetails === null) {
    return null;
  }

  return (
    <>
      <div className={classes.page}>
        <DisplayAdsEditor
          showSummaryBarControl
          showRecommendationsBanner
          i18n={i18n}
          hasFetchBrandKitData
          hasUpdateBrandKitData
          brandkitApiState={brandkitApiState}
          brandKitDataService={brandKitDataService}
          templateData={templateDetails.templateGroupDetails}
          url={templateDetails.url}
          displayTemplatesDataService={displayTemplatesDataService}
          dataService={dataService}
          containerClassName={classes.editorContainer}
          serverContext={serverContext}
          preferenceService={preferenceService}
          perfMarker={displayTemplateCreatePagePerfMarker}
          aid={aid}
          cid={cid}
          oDataConfig={oDataConfig}
          permissions={permissions}
          saveInNewFolder
          onSaveSuccess={({ count }) => {
            notify({ count });
            dataService.reloadFolderPathTreeItem(['ROOT']);
            navigation.gotoImagesPage({ removeSearchQuery: [AdditionalQueries.TemplateGroupId] });
          }}
          navigation={navigation}
          instrumentation={instrumentation}
          scenarioName="CreateDisplayTemplate"
          activityName="CreateDisplayTemplate"
          setDataLoaded={setDataLoaded}
        />
      </div>
    </>
  );
};

CreateDisplayTemplatePage.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  dataService: PropTypes.shape({
    loadData: PropTypes.func,
    reloadFolderPathTreeItem: PropTypes.func,
  }),
  notificationApi: PropTypes.shape({
    showWarning: PropTypes.func,
  }),
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  adsStudioUserPreferences: PropTypes.shape({}),
  reload: PropTypes.shape({}),
  brandKitDataService: PropTypes.shape({
    getBrandKits: PropTypes.func,
    createBrandKits: PropTypes.func,
    updateBrandKit: PropTypes.func,
    brandkitLoading: PropTypes.bool,
  }).isRequired,
  brandkitApiState: PropTypes.shape({
    brandkitResp: PropTypes.shape({}),
    brandkitError: PropTypes.shape({}),
    cache: PropTypes.shape({}),
    brandkitLoading: PropTypes.bool,
  }).isRequired,
  permissions: PropTypes.shape({ }).isRequired,
  aid: PropTypes.number.isRequired,
  cid: PropTypes.number.isRequired,
  pagePerfMarker: PropTypes.shape({}).isRequired,
  oDataConfig: PropTypes.shape({}).isRequired,
  serverContext: PropTypes.shape({}).isRequired,
  preferenceService: PropTypes.shape({}).isRequired,
  instrumentation: PropTypes.shape({
    addScenario: PropTypes.func,
  }).isRequired,
  navigation: PropTypes.shape({
    gotoDisplayTemplatesPage: PropTypes.func,
    gotoImagesPage: PropTypes.func,
    removeSearchQuery: PropTypes.func,
  }).isRequired,
  displayTemplatesDataService: PropTypes.shape({
    editDisplayAdsAssetRecommendations: PropTypes.func,
    fetchDisplayAdsTemplateGroupDetails: PropTypes.func,
  }).isRequired,
  setDataLoaded: PropTypes.func.isRequired,
  search: PropTypes.string.isRequired,
};

CreateDisplayTemplatePage.defaultProps = {
  dataService: {},
  notificationApi: {},
  adsStudioUserPreferences: undefined,
  reload: null,
};

export const CreateDisplayTemplatePageComponent = CreateDisplayTemplatePage;
